/*
 * Global money helpers.
 *
 * The theme used to string-substitute new amounts into a single `{{ price | money }}`
 * sample captured at page load, which broke as soon as a market used a different
 * currency or separator convention. Everything now goes through
 * `window.theme.formatMoney(cents)`, which renders `shop.money_format` the same way
 * Shopify does server-side.
 */
window.theme = window.theme || {};

(function () {
  var DEFAULT_FORMAT = '${{amount}}';

  function defaultTo(value, fallback) {
    return value === null || value === undefined || value === '' ? fallback : value;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');

    if (isNaN(number) || number === null) return '0';

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.');
    var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  /**
   * Render an amount in cents using a Shopify money format string.
   * @param {number|string} cents
   * @param {string} [format] defaults to `window.theme.moneyFormat`
   * @returns {string}
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    var moneyFormat = format || window.theme.moneyFormat || DEFAULT_FORMAT;
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = moneyFormat.match(placeholderRegex);

    switch (match && match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    if (!match) return value;
    return moneyFormat.replace(placeholderRegex, value);
  }

  /**
   * Best-effort parse of a rendered money string back into cents.
   * Handles both "1,234.56" and "1.234,56" shapes as well as bare "39.90".
   * @param {string|number} value
   * @returns {number} cents, or NaN when nothing numeric was found
   */
  function parseMoney(value) {
    if (typeof value === 'number') return Math.round(value);

    var raw = String(value === null || value === undefined ? '' : value)
      .replace(/[^0-9.,]/g, '')
      .trim();
    if (!raw) return NaN;

    var lastSeparator = Math.max(raw.lastIndexOf('.'), raw.lastIndexOf(','));
    var decimals = '';
    var units = raw;

    if (lastSeparator !== -1) {
      var tail = raw.slice(lastSeparator + 1);
      // A trailing group of exactly 1-2 digits is a decimal part; 3 digits is a
      // thousands group ("1.234" is 1234 units, not 1.234).
      if (tail.length > 0 && tail.length < 3 && /^\d+$/.test(tail)) {
        decimals = tail.length === 1 ? tail + '0' : tail;
        units = raw.slice(0, lastSeparator);
      }
    }

    units = units.replace(/[^0-9]/g, '');
    if (!units && !decimals) return NaN;

    return parseInt(units || '0', 10) * 100 + parseInt(decimals || '0', 10);
  }

  /**
   * Accepts either cents (number) or an already-rendered money string and always
   * returns a money string in the active market's format. Lets legacy call sites
   * that pass rendered text keep working.
   */
  function toMoney(value, format) {
    if (typeof value === 'number') return formatMoney(value, format);

    var cents = parseMoney(value);
    if (isNaN(cents)) return String(value);
    return formatMoney(cents, format);
  }

  window.theme.formatMoney = formatMoney;
  window.theme.parseMoney = parseMoney;
  window.theme.toMoney = toMoney;
})();
